import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStudioAuthenticatedContext } from '../../../../../context/StudioContext';
import * as styles from './DashboardPopularSection.styles';

interface PopularItem {
  id: string;
  title: string;
  artist?: string;
  type: 'Collection' | 'Track' | 'Artist';
  plays: number;
  uses: number;
  shares: number;
  total: number;
}

const formatNumber = (num: number): string => {
  return num.toLocaleString();
};

const parseEntityName = (
  entityName: string,
  entityType: 'Collection' | 'Track' | 'Artist',
): { title: string; artist?: string } => {
  if (entityType === 'Track' && entityName.includes(' - ')) {
    const parts = entityName.split(' - ');
    if (parts.length >= 2) {
      return {
        title: parts[0].trim(),
        artist: parts.slice(1).join(' - ').trim(),
      };
    }
  }
  return { title: entityName };
};

export const DashboardPopularSection = () => {
  const { backendClient, selectedAppSet } = useStudioAuthenticatedContext();
  const [popularItems, setPopularItems] = useState<PopularItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPopularItems = async () => {
      if (!selectedAppSet) {
        setError('No application selected');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await backendClient.getPopularItems({
          appId: selectedAppSet.productionApp.id,
        });

        if (!response || !response.data || !Array.isArray(response.data)) {
          setError('Invalid response from server');
          setPopularItems([]);
          return;
        }

        const items: PopularItem[] = response.data.map((item, index) => {
          const { title, artist } = parseEntityName(
            item.entity_name,
            item.entity_type as 'Collection' | 'Track' | 'Artist',
          );

          return {
            id: `popular-${index}`,
            title,
            artist,
            type: item.entity_type as 'Collection' | 'Track' | 'Artist',
            plays: item.total_plays || 0,
            uses: item.total_adds || 0,
            shares: item.total_shares || 0,
            total: item.total_engagements || 0,
          };
        });

        setPopularItems(items);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : 'An error occurred while fetching popular items',
        );
        setPopularItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPopularItems();
  }, [backendClient, selectedAppSet]);

  return (
    <Box>
      <Box sx={styles.headerContainer}>
        <Typography variant="h4" color="text.primary" sx={styles.titleTypography}>
          Popular
        </Typography>
        <Button
          component={Link}
          to="/analytics"
          variant="contained"
          color="primary"
          sx={styles.detailsButton}
        >
          Details
        </Button>
      </Box>

      <Card elevation={0} sx={styles.card}>
        <CardContent sx={styles.cardContent}>
          {loading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
              }}
            >
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
              }}
            >
              <Typography color="error">{error}</Typography>
            </Box>
          ) : popularItems.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: 200,
              }}
            >
              <Typography color="text.secondary">
                No popular items available for this month.
              </Typography>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={styles.headerCellWithWidth}>
                    {/* Empty header for item column */}
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    <Typography variant="body2" color="text.primary" sx={styles.headerTypography}>
                      Plays
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    <Typography variant="body2" color="text.primary" sx={styles.headerTypography}>
                      Uses
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    <Typography variant="body2" color="text.primary" sx={styles.headerTypography}>
                      Shares
                    </Typography>
                  </TableCell>
                  <TableCell sx={styles.headerCell}>
                    <Typography variant="body2" color="text.primary" sx={styles.headerTypography}>
                      Total
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {popularItems.map((item, index) => (
                  <TableRow
                    key={item.id}
                    sx={
                      index < popularItems.length - 1
                        ? styles.tableRow
                        : styles.lastTableRow
                    }
                  >
                    <TableCell sx={styles.dataCell}>
                      <Box sx={styles.itemContainer}>
                        <Typography
                          variant="body1"
                          color="text.primary"
                          sx={styles.itemTypography}
                        >
                          {item.title}
                          {item.artist && ` - ${item.artist}`}
                        </Typography>
                        <Box sx={styles.typeBadge}>
                          <Typography
                            variant="caption"
                            color="text.primary"
                            sx={styles.badgeTypography}
                          >
                            {item.type}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={styles.dataCell}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={styles.metricTypography}
                      >
                        {formatNumber(item.plays)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.dataCell}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={styles.metricTypography}
                      >
                        {formatNumber(item.uses)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.dataCell}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={styles.metricTypography}
                      >
                        {formatNumber(item.shares)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={styles.dataCell}>
                      <Typography
                        variant="body2"
                        color="text.primary"
                        sx={styles.metricTypography}
                      >
                        {formatNumber(item.total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
